import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "../styles/PoemArabicWater.css";
import videoSource from "../assets/riverbg3.mov";

const PoemArabicWater: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Ensure video plays on load
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.log("Video autoplay failed:", error);
      });
    }
  }, []);

  // Parse poem data - split by --- to get left and right columns
  const poemLines = [
    { right: "ان كان سرا للحياة", left: "ان كان ريا للظماء" },
    { right: "ان كان طهرا للابدان", left: "ان كان مثوى للرخاء" },
    { right: "فهو الذي يهطل قطرا", left: "وينحدر من السماء" },
    { right: "يشق الارض انهارا", left: "ويبقى اصل البقاء" },
    { refrain: true },
    { right: "فنهر الزمان جار", left: "لنمير ام اجاج" },
    { right: "يصب في بحر لا فناء", left: "سلسبيل والاجن" },
    { divider: true },
    { right: "من قمم الجبال الشامخة", left: "ينبوع صاف زلال نمير" },
    { right: "صاعد من الاعالي الراسخة", left: "رفيع الاصل عذب نبيل" },
    { right: "وفي الرمال القاحلة", left: "عين تهدي الضال كالنور" },
    { right: "وسط الظلمة الشاملة", left: "تسقي الارض والصدور" },
    { right: "ونهر يجري حاملا", left: "خيره لكل واد" },
    { right: "يسير معطيا فيضا", left: "للهادي والغادي" },
    { right: "وبحر واسع الارجاء", left: "يضم الجميع سواء" },
    { right: "كثير الانواء", left: "وموطن لكل الضعفاء" },
    { refrain: true },
    { right: "فنهر الزمان جار", left: "لنمير ام اجاج" },
    { right: "يصب في بحر لا فناء", left: "سلسبيل والاجن" },
    { divider: true },
    { right: "ماء يجري مندفعا", left: "مشتاقا يبتغي مداه" },
    { right: "يتهافت عبر الصخور", left: "حتى يبلغ منتهاه" },
    { right: "وماء راكد اجن", left: "خامد جامد فاتر عديم النماء" },
    { right: "لا حراك ولا حياة", left: "ميت وان طال له البقاء" },
    { refrain: true },
    { right: "فنهر الزمان جار", left: "لنمير ام اجاج" },
    { right: "يصب في بحر لا فناء", left: "سلسبيل والاجن" },
    { divider: true },
    { right: "فترى النهر جاريا", left: "مقسما للبلاد" },
    { right: "يسرع من واد لواد", left: "حتى اتى صخر الاوتاد" },
    { right: "قالوا: يا ماء ما لك", left: "اتغلب الصخر العسير؟" },
    { right: "فقال: افلا تراني فيه انحت", left: "بصبر وعزم لا نظير؟" },
    { right: "وترى ماء رابضا", left: "لا يتحرك الا ارتعاشا" },
    { right: "لا يتغير الا بالعبث", left: "ويزداد قذارة وانطفاشا" },
    { right: "فقلت: يا ماء ويحك", left: "ما لي اراك براكد؟" },
    { right: "فقال: ما لي امشي", left: "ومستقري هاهنا واجد؟" },
    { refrain: true },
    { right: "فنهر الزمان جار", left: "لنمير ام اجاج" },
    { right: "يصب في بحر لا فناء", left: "سلسبيل والاجن" },
    { divider: true },
    { right: "هل يعقل ان ماءا يرجع", left: "اضعافا واصله العطاء؟" },
    { right: "هل يعقل ان ماءا يسكر", left: "عقولا وهو لها دواء؟" },
    { right: "هل يعقل ان ماءا ينحرف", left: "مسارا وطريقه الاستواء؟" },
    { right: "هل يعقل ان ماءا يطمع", left: "لنفسه وخلق للرعاء؟" },
    { right: "هل يعقل ان ماءا يظن", left: "نفسه صلبا او هواء؟" },
    { right: "هل يعقل ان ماءا ينكر", left: "مصيره والسماء لقاء؟" },
    { divider: true },
    { right: "فكل ماء عائد لمنزله", left: "كل يبخر كل فان" },
    { right: "فاما ان كان فيضانا", left: "بعزيمته نحت الجبال" },
    { right: "او كان للسيل غثاء", left: "لا يملك سوى الاماني" },
    { right: "فاما ان كان فيضانا", left: "او كان للسيل غثاء" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.3,
      },
    },
  };

  const verseVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <div className="poem-water-page">
      {/* Background Video */}
      <div className="video-background">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className="background-video"
          src={videoSource}
        >
          {/* Fallback message for browsers that don't support video */}
          Your browser does not support the video tag.
        </video>
        <div className="video-overlay"></div>
      </div>

      {/* Content */}
      <div className="poem-content-wrapper">
        <motion.div
          className="poem-title-section"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <h1 className="poem-main-title">الماء</h1>
          <div className="title-ornament"></div>
        </motion.div>

        <motion.div
          className="poem-verses-container"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {poemLines.map((line, index) => {
            if ("refrain" in line && line.refrain) {
              return (
                <motion.div
                  key={`refrain-marker-${index}`}
                  className="refrain-marker"
                  variants={verseVariants}
                >
                  <div className="refrain-line"></div>
                </motion.div>
              );
            }

            if ("divider" in line && line.divider) {
              return (
                <motion.div
                  key={`divider-${index}`}
                  className="stanza-divider"
                  variants={verseVariants}
                ></motion.div>
              );
            }

            return (
              <motion.div
                key={index}
                className="poem-verse-line"
                variants={verseVariants}
              >
                <div className="verse-left">{line.left}</div>
                <div className="verse-separator">⁂</div>
                <div className="verse-right">{line.right}</div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Closing Ornament */}
        <motion.div
          className="poem-closing"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2, duration: 1 }}
        >
          <div className="closing-ornament">✦</div>
        </motion.div>
      </div>
    </div>
  );
};

export default PoemArabicWater;
